import type { Locale, LocaleStrings } from "../ai_literacy/conceptCopy.js";

export {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  type Locale,
  type LocaleStrings,
  type StemTemplate,
} from "../ai_literacy/conceptCopy.js";

const EN: LocaleStrings = {
  titles: {
    adaptive_core: "The adaptive core: Elo + graph + mastery",
    measured_proof: "Measured, auditable proof of competence",
    your_content: "Your content, not ours — build your own training pool",
    self_hosted: "Self-hosted: your cloud, your data",
    privacy_first: "Privacy by design",
    engagement_loop: "Engagement that sticks",
    open_no_lockin: "Open, cheap, no lock-in",
  },
  blurbs: {
    adaptive_core:
      "Every learner and question carries an Elo rating; a curriculum knowledge graph (PageRank, betweenness, topological order) and a per-concept mastery model pick the next best question — and you can inspect exactly why.",
    measured_proof:
      "Because mastery is tracked per concept and per person, notanyai produces auditable evidence of who has learned what — the thing a training video can never give you.",
    your_content:
      "AI literacy is just the first SubjectPack. The engine is subject-agnostic, so a company builds and manages its own pool of trainings and tests: onboarding, security, compliance, product knowledge.",
    self_hosted:
      "The whole stack — a static front end plus an open Supabase backend — runs inside your own cloud, VPC, or on-prem. No learner data, prompt, or policy ever has to leave your perimeter.",
    privacy_first:
      "Row-level security, anonymized long-term retention, self-service account deletion, and passwordless magic-link auth: privacy is how the data layer is built, not a setting bolted on.",
    engagement_loop:
      "Daily streaks, four learning formats including three mini-games, a quest path, and selectable GameMaster styles keep learners coming back — the consumer-app loop applied to your content.",
    open_no_lockin:
      "No proprietary content format and no mandatory hosted dependency; production runs comfortably on a single small ARM VM. You are never trapped.",
  },
  stems: [
    (b) => `Which notanyai benefit is this about?\n\n“${b}”`,
    (b) => `Which capability best matches this description?\n\n${b}`,
    (b) => `In the notanyai pitch, this paragraph describes which benefit?\n\n${b}`,
    (b) => `You’re taking notes on notanyai. This sounds like which benefit?\n\n«${b}»`,
    (b) => `From the product map, this belongs to which benefit?\n\n${b}`,
    (b) => `Match the description to the right benefit:\n\n${b}`,
  ],
};

const ES: LocaleStrings = {
  titles: {
    adaptive_core: "El núcleo adaptativo: Elo + grafo + dominio",
    measured_proof: "Prueba de competencia medible y auditable",
    your_content: "Tu contenido, no el nuestro — crea tu propio catálogo de formación",
    self_hosted: "Autoalojado: tu nube, tus datos",
    privacy_first: "Privacidad por diseño",
    engagement_loop: "Una experiencia que engancha",
    open_no_lockin: "Abierto, económico, sin dependencia forzada",
  },
  blurbs: {
    adaptive_core:
      "Cada persona y cada pregunta tienen una puntuación Elo; un grafo de conocimiento del temario (PageRank, intermediación, orden topológico) y un modelo de dominio por concepto eligen la siguiente pregunta, y puedes ver exactamente por qué.",
    measured_proof:
      "Como el dominio se mide por concepto y por persona, notanyai genera evidencia auditable de quién ha aprendido qué: lo que un vídeo de formación nunca podrá darte.",
    your_content:
      "La alfabetización en IA es solo el primer SubjectPack. El motor es independiente del tema, así que una empresa crea y gestiona su propio catálogo de formaciones y exámenes: incorporación, seguridad, cumplimiento, producto.",
    self_hosted:
      "Toda la pila —un frontend estático más un backend Supabase abierto— se ejecuta en tu propia nube, VPC o servidores propios. Ningún dato, prompt ni política tiene que salir de tu perímetro.",
    privacy_first:
      "Seguridad a nivel de fila, retención anonimizada, borrado de cuenta autoservicio y acceso sin contraseña por enlace mágico: la privacidad es cómo se construye la capa de datos, no un ajuste añadido.",
    engagement_loop:
      "Rachas diarias, cuatro formatos de práctica con tres minijuegos, una ruta de misión y estilos de GameMaster mantienen el hábito: el bucle de las apps de consumo aplicado a tu contenido.",
    open_no_lockin:
      "Sin formato de contenido propietario ni dependencia alojada obligatoria; la producción corre en una sola VM ARM pequeña. Nunca quedas atrapado.",
  },
  stems: [...EN.stems],
};

const EL: LocaleStrings = {
  titles: {
    adaptive_core: "Ο προσαρμοστικός πυρήνας: Elo + γράφος + mastery",
    measured_proof: "Μετρήσιμη, ελέγξιμη απόδειξη επάρκειας",
    your_content: "Δικό σου περιεχόμενο, όχι δικό μας — φτιάξε το δικό σου training pool",
    self_hosted: "Self-hosted: το δικό σου cloud, τα δικά σου δεδομένα",
    privacy_first: "Privacy by design",
    engagement_loop: "Εμπειρία που κρατά τους χρήστες ενεργούς",
    open_no_lockin: "Ανοιχτό, οικονομικό, χωρίς lock-in",
  },
  blurbs: {
    adaptive_core:
      "Κάθε learner και κάθε ερώτηση έχει Elo rating· ένας knowledge graph του curriculum (PageRank, betweenness, topological order) και ένα per-concept mastery model επιλέγουν την επόμενη καλύτερη ερώτηση — και μπορείς να δεις ακριβώς γιατί.",
    measured_proof:
      "Επειδή το mastery παρακολουθείται ανά concept και ανά άτομο, το notanyai δίνει ελέγξιμα στοιχεία για το ποιος έμαθε τι — κάτι που ένα απλό training video δεν μπορεί να προσφέρει.",
    your_content:
      "Το AI literacy είναι μόνο το πρώτο SubjectPack. Ο πυρήνας είναι subject-agnostic, ώστε μια εταιρεία να δημιουργεί και να διαχειρίζεται το δικό της pool από trainings και tests: onboarding, security, compliance, product knowledge.",
    self_hosted:
      "Ολόκληρο το stack — static front end + open Supabase backend — τρέχει στο δικό σου cloud, VPC ή on-prem. Κανένα learner data, prompt ή policy δεν χρειάζεται να φύγει από το perimeter σου.",
    privacy_first:
      "Row-level security, ανωνυμοποιημένη διατήρηση ιστορικού, self-service διαγραφή λογαριασμού και passwordless magic-link auth: η ιδιωτικότητα είναι μέρος της αρχιτεκτονικής, όχι έξτρα ρύθμιση.",
    engagement_loop:
      "Daily streaks, τέσσερα learning formats με τρία mini-games, quest path και επιλέξιμα GameMaster styles κρατούν τους χρήστες ενεργούς — ο engagement loop των consumer apps πάνω στο δικό σου περιεχόμενο.",
    open_no_lockin:
      "Χωρίς proprietary content format και χωρίς υποχρεωτικό hosted dependency· το production τρέχει άνετα σε ένα μικρό ARM VM. Δεν εγκλωβίζεσαι.",
  },
  stems: [
    (b) => `Σε ποιο όφελος του notanyai αναφέρεται αυτό;\n\n«${b}»`,
    (b) => `Ποια δυνατότητα ταιριάζει καλύτερα σε αυτή την περιγραφή;\n\n${b}`,
    (b) => `Στο pitch του notanyai, αυτή η παράγραφος περιγράφει ποιο όφελος;\n\n${b}`,
    (b) => `Κρατάς σημειώσεις για το notanyai. Αυτό αντιστοιχεί σε ποιο όφελος;\n\n«${b}»`,
    (b) => `Από τον χάρτη προϊόντος, αυτό ανήκει σε ποιο όφελος;\n\n${b}`,
    (b) => `Ταίριαξε την περιγραφή με το σωστό όφελος:\n\n${b}`,
  ],
};

export const COPY: Readonly<Record<Locale, LocaleStrings>> = { en: EN, es: ES, el: EL };

export function getCopy(locale: Locale = "en"): LocaleStrings {
  return COPY[locale] ?? EN;
}

export const CONCEPT_TITLES: Readonly<Record<string, string>> = EN.titles;
export const CONCEPT_BLURBS: Readonly<Record<string, string>> = EN.blurbs;

export function allConceptCopyIds(): string[] {
  return Object.keys(EN.titles);
}
